<template>
    <div v-if="isBlocked" class="mask">
        <div class="card">
            <h2>Landscape recommended</h2>
            <font-awesome-icon class="icon" :icon="['fad', 'mobile']" size="5x" />
            <p>This page is best used in landscape on mobile devices. We recommend rotating your device for the best experience.</p>
            <prime-button label="Continue anyway" @click="dismiss" />
        </div>
    </div>
</template>

<script setup lang="ts">
import {
    computed,
    onBeforeUnmount,
    watch,
    ref,
} from 'vue';
import { useRoute } from 'vue-router';
import PrimeButton from 'primevue/button';
import { SCREEN_SIZES } from '@/constants/ScreenSizes.ts';

type LegacyMediaQueryList = MediaQueryList & {
    addListener: (listener: (event: MediaQueryListEvent) => void) => void;
    removeListener: (listener: (event: MediaQueryListEvent) => void) => void;
};

const route = useRoute();

const isEnabled = computed<boolean>(() => route.matched.some(r => r.meta?.blockSmallPortrait));

const matches = ref<boolean>(false);
const dismissed = ref<boolean>(false);
let mql: MediaQueryList | null = null;

const query = `(max-width: ${SCREEN_SIZES.MOBILE_MAX_WIDTH}px) and (orientation: portrait)`;

function updateMatch (): void {
    if (!mql) return;
    matches.value = mql.matches;
}

function handleMediaQueryChange (): void {
    updateMatch();
}

function attach (): void {
    if (typeof window === 'undefined') return;

    mql = window.matchMedia(query);
    updateMatch();

    if (typeof mql.addEventListener === 'function') mql.addEventListener('change', handleMediaQueryChange);
    else (mql as unknown as LegacyMediaQueryList).addListener(handleMediaQueryChange);
}

function detach (): void {
    if (!mql) return;

    if (typeof mql.removeEventListener === 'function') mql.removeEventListener('change', handleMediaQueryChange);
    else (mql as unknown as LegacyMediaQueryList).removeListener(handleMediaQueryChange);

    mql = null;
    matches.value = false;
}

watch(isEnabled, (enabled) => {
    detach();
    dismissed.value = false;
    if (enabled) attach();
}, { immediate: true });

watch(() => route.fullPath, () => {
    dismissed.value = false;
});

watch(matches, (newVal) => {
    if (!newVal) dismissed.value = false;
});

onBeforeUnmount(() => {
    detach();
});

function dismiss (): void {
    dismissed.value = true;
}

const isBlocked = computed<boolean>(() => isEnabled.value && matches.value && !dismissed.value);
</script>

<style scoped lang="scss">

$sideNavWidth: 50px;

.mask {
    position: fixed;
    inset: 0;
    z-index: 3;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
}

.card {
    position: absolute;
    background-color: var(--neutral0);
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--extraLargeSpacing);
    padding: var(--extraLargeSpacing) var(--largeSpacing);
    width: calc(100vw - $sideNavWidth);

    p {
        text-align: center;
    }
}

.icon {
    display: inline-flex;
    transform-origin: 50% 50%;
    will-change: transform, opacity;
    animation: orientationRotateFade 2.4s ease-in-out infinite;
}

@keyframes orientationRotateFade {
    0% {
        opacity: 0;
        transform: rotate(0deg);
    }

    20% {
        opacity: 1;
        transform: rotate(0deg);
    }

    60% {
        opacity: 1;
        transform: rotate(90deg);
    }

    85% {
        opacity: 0;
        transform: rotate(90deg);
    }

    100% {
        opacity: 0;
        transform: rotate(90deg);
    }
}

</style>
